import { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import DashboardWidgetCard from '../../Dashboard/DashboardWidgetCard';

const FRAUD_TYPE_LABELS = {
  duplicate_redemption: 'Mehrfache Einlösung',
  rapid_redemptions: '3x Nutzung < 2 Stunden',
  same_name_multiple_emails: 'Gleicher Name, mehrere E-Mails',
  redeemer_email_mismatch: 'Einlösung mit fremder E-Mail',
};

// Fraud alerts from the automated marketing voucher detection (concept
// section 7) with resolve/dismiss actions and optional voucher locking.
export default function FraudAlertsWidget({
  alerts,
  canUpdate,
  busyId,
  loading,
  error,
  onRetry,
  onPatch,
}) {
  const [lockChecked, setLockChecked] = useState({});
  const openAlerts = (alerts || []).filter(alert => alert.status === 'open');
  const recentResolved = (alerts || []).filter(alert => alert.status !== 'open').slice(0, 3);

  return (
    <DashboardWidgetCard
      title={`Fraud-Alerts${openAlerts.length ? ` (${openAlerts.length} offen)` : ''}`}
      icon='GppMaybe'
      accent={openAlerts.length ? 'error' : 'success'}
      loading={loading}
      error={error}
      onRetry={onRetry}
    >
      <Stack spacing={1.5} sx={{ flexGrow: 1 }}>
        {openAlerts.length === 0 && (
          <Typography variant='body2' color='text.secondary'>
            Keine offenen Fraud-Alerts — Ziel laut Konzept: &lt; 2 % verdächtige Aktivität.
          </Typography>
        )}
        {openAlerts.map(alert => (
          <Box
            key={alert._id}
            sx={{
              p: 1.5,
              borderRadius: 1.5,
              border: theme => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 0.5, flexWrap: 'wrap' }}>
              <Chip size='small' color='error' label={FRAUD_TYPE_LABELS[alert.type] || alert.type} />
              <Typography variant='caption' color='text.secondary'>
                {dayjs(alert.createdAt).format('DD.MM.YYYY HH:mm')}
              </Typography>
              {alert.voucherCode && (
                <Typography variant='caption' sx={{ fontFamily: 'monospace' }}>
                  {alert.voucherCode}
                </Typography>
              )}
            </Stack>
            <Typography variant='body2' sx={{ mb: 1 }}>
              {alert.details}
            </Typography>
            {canUpdate && (
              <Stack direction='row' spacing={1} alignItems='center' sx={{ flexWrap: 'wrap' }}>
                {alert.voucherId && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size='small'
                        checked={Boolean(lockChecked[alert._id])}
                        onChange={event =>
                          setLockChecked(previous => ({
                            ...previous,
                            [alert._id]: event.target.checked,
                          }))
                        }
                      />
                    }
                    label={<Typography variant='caption'>Code sperren</Typography>}
                  />
                )}
                <Button
                  size='small'
                  variant='outlined'
                  disabled={busyId === alert._id}
                  onClick={() =>
                    onPatch(alert, { status: 'resolved', lockVoucher: Boolean(lockChecked[alert._id]) })
                  }
                >
                  Klären
                </Button>
                <Button
                  size='small'
                  disabled={busyId === alert._id}
                  onClick={() =>
                    onPatch(alert, { status: 'dismissed', lockVoucher: false })
                  }
                >
                  Verwerfen
                </Button>
              </Stack>
            )}
          </Box>
        ))}
        {recentResolved.length > 0 && (
          <Typography variant='caption' color='text.secondary'>
            Zuletzt bearbeitet:{' '}
            {recentResolved
              .map(alert => `${FRAUD_TYPE_LABELS[alert.type] || alert.type} (${alert.status === 'resolved' ? 'geklärt' : 'verworfen'})`)
              .join(' · ')}
          </Typography>
        )}
      </Stack>
    </DashboardWidgetCard>
  );
}
